import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, Building2, BookOpen, ClipboardList, Plus, Trash2, Edit,
  BarChart3, Send, Brain, AlertTriangle, CheckCircle2, Clock,
  TrendingUp, Award, Target, ChevronDown, ChevronUp, X, Loader2,
  Shield, Eye, UserCheck, Activity, Zap,FileText,
  Upload, PackageOpen ,  UserPlus, UserMinus, UserCog, Mail, MailCheck, FileSpreadsheet,
  ToggleLeft, ToggleRight, Download, RefreshCw, Search, Calendar
} from 'lucide-react';
import DiplomaConfigModal from './DiplomaConfigModal'; 
import ModalOverlay from 'components/ModalOverlay';
/* ─── GLOBAL STYLES ─────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

  .oa-root {
    font-family: 'DM Sans', sans-serif;
    color: #ffffff;
    min-height: 100vh;
    padding: 32px 28px;
    box-sizing: border-box;
    animation: oa-fadeIn 0.4s ease;
  }
  @keyframes oa-fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }

  /* ── GLASS PANELS ── */
  .oa-panel {
    background: rgba(8, 6, 24, 0.7);
    backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 4px 32px rgba(0,0,0,0.4), inset 0 0 0 0.5px rgba(255,255,255,0.04);
  }

  /* ── STAT CARDS ── */
  .oa-stat {
    background: rgba(8, 6, 24, 0.6);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 16px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    transition: border-color 0.2s;
  }
  .oa-stat:hover { border-color: rgba(255,255,255,0.14); }

  /* ── TABS ── */
  .oa-tab {
    padding: 12px 18px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    border-bottom: 2px solid transparent;
    color: rgba(255,255,255,0.35);
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 7px;
    background: none;
    border-top: none;
    border-left: none;
    border-right: none;
    font-family: 'DM Sans', sans-serif;
    white-space: nowrap;
  }
  .oa-tab:hover { color: rgba(255,255,255,0.7); }
  .oa-tab.active { color: #00FFFF; border-bottom-color: #00FFFF; }

  /* ── INPUTS ── */
  .oa-input {
    width: 100%;
    padding: 11px 15px;
    border-radius: 11px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.09);
    color: #fff;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.25s;
    box-sizing: border-box;
  }
  .oa-input:focus { outline: none; border-color: #00FFFF; box-shadow: 0 0 12px rgba(0,255,255,0.15); }
  .oa-input::placeholder { color: rgba(255,255,255,0.28); }
  select.oa-input option { background: #060810; }

  /* ── BUTTONS ── */
  .oa-btn-cyan {
    background: #00FFFF; color: #000; font-weight: 800; border: none;
    border-radius: 10px; padding: 10px 20px; font-size: 11px;
    letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    box-shadow: 0 0 14px rgba(0,255,255,0.35);
    transition: all 0.2s; display: flex; align-items: center; gap: 7px;
  }
  .oa-btn-cyan:hover { background: #66FFFF; box-shadow: 0 0 22px rgba(0,255,255,0.55); transform: translateY(-1px); }
  .oa-btn-cyan:disabled { background: rgba(0,255,255,0.1); color: rgba(0,255,255,0.3); box-shadow: none; cursor: not-allowed; transform: none; }

  .oa-btn-ghost {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.6);
    border-radius: 10px; padding: 9px 16px; font-size: 11px;
    letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;
    font-family: 'DM Sans', sans-serif; transition: all 0.2s;
    display: flex; align-items: center; gap: 6px;
  }
  .oa-btn-ghost:hover { background: rgba(255,255,255,0.1); color: #fff; }

  .oa-btn-danger {
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.3);
    color: #f87171;
    border-radius: 8px; padding: 7px 10px; cursor: pointer;
    transition: all 0.2s; display: flex; align-items: center;
    font-family: 'DM Sans', sans-serif;
  }
  .oa-btn-danger:hover { background: rgba(239,68,68,0.2); border-color: rgba(239,68,68,0.5); }

  /* ── BADGES ── */
  .oa-badge {
    padding: 3px 10px; border-radius: 6px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.1em;
    text-transform: uppercase; white-space: nowrap;
  }

  /* ── LIST ROWS ── */
  .oa-row {
    padding: 16px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    transition: background 0.15s;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .oa-row:last-child { border-bottom: none; }
  .oa-row:hover { background: rgba(255,255,255,0.02); }

  /* ── MODAL ── */
  .oa-modal-overlay {
    position: fixed; inset: 0; z-index: 9999;
    background: rgba(0,0,0,0.8);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center; padding: 20px;
  }
  .oa-modal {
    background: #060810;
    border: 1px solid rgba(0,255,255,0.2);
    border-radius: 22px;
    width: 100%; max-width: 600px; max-height: 90vh;
    display: flex; flex-direction: column;
    box-shadow: 0 0 60px rgba(0,255,255,0.12);
    overflow: hidden;
  }
  .oa-modal-header {
    padding: 22px 26px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    background: linear-gradient(to right, rgba(0,255,255,0.06), transparent);
    display: flex; align-items: center; justify-content: space-between;
    flex-shrink: 0;
  }
  .oa-modal-body { padding: 22px 26px; overflow-y: auto; flex: 1; }
  .oa-modal-footer {
    padding: 16px 26px;
    border-top: 1px solid rgba(255,255,255,0.07);
    background: rgba(0,0,0,0.3);
    display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0;
  }

  /* ── CHART BAR ── */
  .oa-bar-track {
    background: rgba(255,255,255,0.05);
    border-radius: 6px; height: 8px; overflow: hidden; flex: 1;
  }
  .oa-bar-fill { height: 100%; border-radius: 6px; transition: width 0.6s ease; }

  /* ── EMPLOYEE PROGRESS TABLE ── */
  .oa-table-row {
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px 100px;
    gap: 12px;
    align-items: center;
    padding: 12px 20px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    font-size: 12px;
  }
  .oa-table-row:hover { background: rgba(255,255,255,0.02); }
  .oa-table-header {
    display: grid;
    grid-template-columns: 1fr 80px 80px 80px 100px;
    gap: 12px;
    padding: 10px 20px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.15em;
    text-transform: uppercase; color: rgba(255,255,255,0.3);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  /* Scrollbar */
  .oa-scroll::-webkit-scrollbar { width: 4px; }
  .oa-scroll::-webkit-scrollbar-track { background: transparent; }
  .oa-scroll::-webkit-scrollbar-thumb { background: rgba(0,255,255,0.2); border-radius: 4px; }

  /* ── AI RADAR ── */
  .oa-ai-item {
    padding: 12px 16px;
    border-radius: 12px;
    background: rgba(239,68,68,0.06);
    border: 1px solid rgba(239,68,68,0.15);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
    /* ── EMPLOYEE MANAGEMENT ── */
.oa-toggle {
  width: 38px; height: 21px; border-radius: 99px; border: none; cursor: pointer;
  transition: background 0.25s; position: relative; flex-shrink: 0;
}
.oa-toggle::after {
  content: ''; position: absolute; top: 3px; left: 3px;
  width: 15px; height: 15px; border-radius: 50%; background: #fff;
  transition: transform 0.25s; box-shadow: 0 1px 4px rgba(0,0,0,0.4);
}
.oa-toggle.on { background: #00FF88; box-shadow: 0 0 10px rgba(0,255,136,0.4); }
.oa-toggle.off { background: rgba(255,255,255,0.12); }
.oa-toggle.on::after { transform: translateX(17px); }

.oa-subtab {
  padding: 7px 14px; font-size: 10px; font-weight: 800; letter-spacing: 0.1em;
  text-transform: uppercase; border-radius: 8px; cursor: pointer;
  transition: all 0.2s; background: none; border: 1px solid transparent;
  font-family: 'DM Sans', sans-serif; color: rgba(255,255,255,0.35);
}
.oa-subtab.active { background: rgba(0,255,255,0.1); border-color: rgba(0,255,255,0.3); color: #00FFFF; }
.oa-subtab:hover:not(.active) { color: rgba(255,255,255,0.6); }

.oa-emp-grid {
  display: grid;
  grid-template-columns: 1fr 160px 120px 90px 110px 170px;
  gap: 10px; align-items: center;
  padding: 11px 20px;
  font-size: 12px;
}
.oa-emp-grid:hover { background: rgba(255,255,255,0.02); }
.oa-emp-header {
  display: grid;
  grid-template-columns: 1fr 160px 120px 90px 110px 170px;
  gap: 10px; padding: 10px 20px;
  font-size: 9px; font-weight: 800; letter-spacing: 0.15em;
  text-transform: uppercase; color: rgba(255,255,255,0.3);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}

.oa-code-btn {
  padding: 5px 11px; border-radius: 7px; font-size: 9px; font-weight: 800;
  letter-spacing: 0.08em; text-transform: uppercase; cursor: pointer;
  font-family: 'DM Sans', sans-serif; transition: all 0.2s; border: none;
  display: flex; align-items: center; gap: 5px; white-space: nowrap;
}
.oa-code-btn.pending {
  background: rgba(255,165,0,0.12); color: #FFA500;
  border: 1px solid rgba(255,165,0,0.3);
}
.oa-code-btn.pending:hover { background: rgba(255,165,0,0.22); }
.oa-code-btn.sent {
  background: rgba(0,255,255,0.08); color: #00FFFF;
  border: 1px solid rgba(0,255,255,0.2);
}
.oa-code-btn.registered { background: transparent; color: rgba(0,255,136,0.7); border: 1px solid rgba(0,255,136,0.2); cursor: default; }
`;

/* ─── HELPERS ────────────────────────────────────────────────────────────── */
const AVATAR_COLORS = ['#00FFFF','#ccff00','#FF7300','#a855f7','#3b82f6','#f43f5e','#10b981'];
const getInitials = (name = '') =>
  name.split(' ').slice(0,2).map(w => w[0]?.toUpperCase() || '').join('') || '?';

const Avatar = ({ name = '', size = 34 }) => {
  const color = AVATAR_COLORS[Math.abs(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: 9, background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: size * 0.33, color: '#000', flexShrink: 0,
      boxShadow: `0 0 10px ${color}55`,
    }}>
      {getInitials(name)}
    </div>
  );
};

const Badge = ({ children, color = '#00FFFF' }) => (
  <span className="oa-badge" style={{
    background: `${color}18`, border: `1px solid ${color}40`, color,
  }}>{children}</span>
);

const StatCard = ({ icon: Icon, label, value, color = '#00FFFF', sub }) => (
  <div className="oa-stat">
    <div style={{
      width: 44, height: 44, borderRadius: 12, flexShrink: 0,
      background: `${color}18`, border: `1px solid ${color}33`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={20} color={color} />
    </div>
    <div>
      <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: '#fff' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

/* ─── MINI BAR CHART ─────────────────────────────────────────────────────── */
const MiniBar = ({ label, value, max = 100, color = '#00FFFF', sub }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
    <div style={{ minWidth: 140, fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </div>
    <div className="oa-bar-track">
      <div className="oa-bar-fill" style={{ width: `${Math.min((value / Math.max(max, 1)) * 100, 100)}%`, background: color }} />
    </div>
    <div style={{ minWidth: 40, fontSize: 12, fontWeight: 800, color, textAlign: 'right' }}>{value}{sub}</div>
  </div>
);

/* ─── DONUT MINI ─────────────────────────────────────────────────────────── */
const DonutMini = ({ pct = 0, color = '#00FFFF', size = 80 }) => {
  const r = 32, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}
      />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
        fill={color} fontSize="14" fontWeight="800" fontFamily="DM Sans, sans-serif">{pct}%</text>
    </svg>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
const OrgAdminPanel = () => {
  const { api, user } = useAuth();
  const orgId = user?.organization_id;

  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  /* ── DATA ── */
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [evaluations, setEvaluations] = useState({ answer_evaluations: [], roleplay_evaluations: [] });
  const [userProgress, setUserProgress] = useState([]);
  const [moduleStats, setModuleStats] = useState([]);
  const [orgData, setOrgData] = useState(null);
  const [showEvalModal, setShowEvalModal] = useState(false);
const [evalModuleId, setEvalModuleId] = useState(null);
const [evalModuleTitle, setEvalModuleTitle] = useState('');
const [generatingRoleplay, setGeneratingRoleplay] = useState(false);
const [roleplayResult, setRoleplayResult] = useState(null);

const [showRenameModal, setShowRenameModal] = useState(false);
const [renameTarget, setRenameTarget] = useState({ id: null, title: '', type: 'module' }); // type: 'module' | 'unit'
const [renameValue, setRenameValue] = useState('');
const [renameSaving, setRenameSaving] = useState(false);
const [showDiplomaConfig, setShowDiplomaConfig] = useState(false);
const [diplomaConfigModule, setDiplomaConfigModule] = useState(null);
const [showOrgDiploma, setShowOrgDiploma] = useState(false);
const [courseActivity, setCourseActivity] = useState({});
const [savingCircle, setSavingCircle] = useState(false);

const saveCircleMode = async (mode) => {
  setSavingCircle(true);
  try {
    await api.put(`/admin/organizations/${orgId}/circle-mode`, { mode });
    setOrgData(p => ({ ...p, circle_mode: mode }));
    toast.success('Configuración del círculo guardada');
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al guardar');
  } finally {
    setSavingCircle(false);
  }
};
const openEditModuleName = (id, title) => {
  setRenameTarget({ id, title, type: 'module' });
  setRenameValue(title);
  setShowRenameModal(true);
};

const saveRename = async () => {
  if (!renameValue.trim()) return;
  setRenameSaving(true);
  try {
    if (renameTarget.type === 'module') {
      await api.patch(`/admin/modules/${renameTarget.id}`, { title: renameValue.trim() });
    } else {
      await api.patch(`/admin/modules/units/${renameTarget.id}`, { title: renameValue.trim() });
    }
    toast.success('Nombre actualizado');
    setShowRenameModal(false);
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al renombrar');
  } finally {
    setRenameSaving(false);
  }
};
const openDiplomaConfig = (moduleId, moduleTitle, existingConfig) => {
  setDiplomaConfigModule({ id: moduleId, title: moduleTitle, diplomaConfig: existingConfig || null });
  setShowDiplomaConfig(true);
};
const generateRoleplay = async () => {
  if (!evalModuleId) return;
  setGeneratingRoleplay(true);
  setRoleplayResult(null);
  try {
    const res = await api.post(`/admin/modules/${evalModuleId}/generate-roleplay`);
    setRoleplayResult(res.data);
    toast.success('Roleplay generado correctamente');
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al generar roleplay');
  } finally {
    setGeneratingRoleplay(false);
  }
};

  /* ── MODALS ── */
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeptModal, setShowDeptModal] = useState(false);

  /* ── EMPLOYEE MANAGEMENT STATE ── */
const [empSearch, setEmpSearch] = useState('');
const [empDeptFilter, setEmpDeptFilter] = useState('all');   // 'all' | 'none' | id
const [empStatusFilter, setEmpStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
const [empCodeFilter, setEmpCodeFilter] = useState('all');   // 'all' | 'pending' | 'sent' | 'registered'
const [empSort, setEmpSort] = useState('name-asc');
const [empSubTab, setEmpSubTab] = useState('manage'); // 'manage' | 'progress'
const [showEmpModal, setShowEmpModal] = useState(false);
const [editingEmp, setEditingEmp] = useState(null); // null = crear, objeto = editar
const [showEmpReport, setShowEmpReport] = useState(false);
const [empReport, setEmpReport] = useState(null);
const [loadingReport, setLoadingReport] = useState(false);
const [generatingReport, setGeneratingReport] = useState({});
const [showBulkModal, setShowBulkModal] = useState(false);
const [showSendCodeModal, setShowSendCodeModal] = useState(false);
const [sendCodeTarget, setSendCodeTarget] = useState({ type: 'all', id: null }); // type: 'all'|'dept'|'user'
const [empForm, setEmpForm] = useState({
  name: '', email: '', department_id: '', dni: '', active: true,
});
const [empModuleFilter, setEmpModuleFilter] = useState('all'); // 'all' | moduleId
const [bulkFile, setBulkFile] = useState(null);
const [bulkPreview, setBulkPreview] = useState([]); // filas parseadas del excel
const [bulkImporting, setBulkImporting] = useState(false);
const [sendingCode, setSendingCode] = useState(false);
const [sendingReminder, setSendingReminder] = useState(false);

  /* ── FORMS ── */
  const [newDept, setNewDept] = useState({ name: '', description: '' });
  const [newAssignment, setNewAssignment] = useState({
  module_id: '', assign_to: 'all', department_ids: [], user_ids: [],
  due_date: '', mandatory: true,
});
  const [scormFile, setScormFile] = useState(null);
  const [uploadingScorm, setUploadingScorm] = useState(false);
  const [scormResult, setScormResult] = useState(null);
const [moduleSettings, setModuleSettings] = useState({});
const [savingModuleSettings, setSavingModuleSettings] = useState({});
const [dueDrafts, setDueDrafts] = useState({});   // { [assignmentId]: { value, notify } }
const [savingDue, setSavingDue] = useState({});

const getDueDraft = (a) => dueDrafts[a.id] ?? { value: a.due_date || '', notify: false };
const setDueDraft = (a, patch) =>
  setDueDrafts(p => ({ ...p, [a.id]: { ...getDueDraft(a), ...patch } }));

const updateModuleSetting = async (moduleId, field, value) => {
  setSavingModuleSettings(p => ({ ...p, [`${moduleId}-${field}`]: true }));
  try {
    await api.put(`/admin/modules/${moduleId}/settings`, { [field]: value });
    setModuleSettings(p => ({ ...p, [moduleId]: { ...p[moduleId], [field]: value } }));
    toast.success('Configuración guardada');
  } catch (err) {
    toast.error('Error: ' + (err.response?.data?.detail || err.message));
  } finally {
    setSavingModuleSettings(p => ({ ...p, [`${moduleId}-${field}`]: false }));
  }
};
  /* ── FETCH ALL ── */
  const fetchAll = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [statsRes, usersRes, modulesRes, assignRes, deptsRes, evalsRes, orgRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get(`/admin/users?organization_id=${orgId}`),
        api.get('/admin/modules'),
        api.get('/admin/assignments'),
        api.get('/admin/departments'),
        api.get('/admin/evaluations'),
        api.get(`/organizations/${orgId}`),
      ]);
      
      const allTestResults = evalsRes.data?.test_results || [];
      setStats(statsRes.data);
      setOrgData(orgRes.data)
      setModules(modulesRes.data || []); // ← FIX: faltaba esta línea (causaba "sin módulos disponibles" en el modal de Asignar Módulo)

      // ── CHANGE 3: Filter out soft-deleted users — only show active ones in the app ──
      const allUsers = usersRes.data || [];
      const activeUsers = allUsers.filter(u =>
        u.is_deleted !== true && u.deleted_at == null && u.status !== 'deleted'
      );
      setUsers(activeUsers);

      const initialSettings = {};
(modulesRes.data || []).forEach(m => {
  initialSettings[m.id] = {
    require_screen_recording: m.requires_screen_record ?? false,
    require_time_tracking: m.require_time_tracking ?? false,
    hours: m.hours ?? 0,
    diploma_enabled: m.diploma_enabled ?? false,
    use_platform_test: m.use_platform_test ?? false,  // ← AÑADIR
  };
});

setModuleSettings(initialSettings);
      setAssignments(assignRes.data || []);
      setDepartments(deptsRes.data || []);
      setEvaluations(evalsRes.data || { answer_evaluations: [], roleplay_evaluations: [] });

      // ── Build per-user progress stats (only active users) ──
      const evalMap = {};
      // Fuente 1: answer_evaluations (roleplays)
      (evalsRes.data?.answer_evaluations || []).forEach(e => {
        if (!evalMap[e.user_id]) evalMap[e.user_id] = { correct: 0, total: 0, ai: 0 };
        evalMap[e.user_id].total++;
        if (e.is_correct) evalMap[e.user_id].correct++;
        if (e.is_ai_generated) evalMap[e.user_id].ai++;
      });
      // Fuente 2: test_results (tests de opción múltiple — la fuente real)
      allTestResults.forEach(r => {
        if (!evalMap[r.user_id]) evalMap[r.user_id] = { correct: 0, total: 0, ai: 0 };
        evalMap[r.user_id].total++;
        if (r.passed || r.percentage >= 70) evalMap[r.user_id].correct++;
      });

      const userStats = activeUsers.map(u => ({
        id: u.id,
        name: u.name,
        department: u.department,
        xp: u.xp || 0,
        level: u.level || 1,
        eval: evalMap[u.id] || { correct: 0, total: 0, ai: 0 },
      })).sort((a, b) => b.xp - a.xp);
      setUserProgress(userStats);

      // ── Build per-module completion stats ──
       const mods = modulesRes.data || [];      const modStats = mods.map(m => {
        // answer_evaluations
        const modEvals = (evalsRes.data?.answer_evaluations || []).filter(e => e.module_id === m.id);
        const aiDetected = modEvals.filter(e => e.is_ai_generated).length;
        // test_results (fuente principal)
        const modTests = allTestResults.filter(r => r.module_id === m.id || r.sub_module_id);
        const modTestsByModule = allTestResults.filter(r => r.module_id === m.id);
        const total = modEvals.length + modTestsByModule.length;
        const correct = modEvals.filter(e => e.is_correct).length
          + modTestsByModule.filter(r => r.passed || r.percentage >= 70).length;
        return {
          id: m.id,
          title: m.title,
          category: m.category,
          totalAnswers: total,
          correctAnswers: correct,
          aiDetected,
          pct: total > 0 ? Math.round((correct / total) * 100) : 0,
        };
      }).sort((a, b) => b.totalAnswers - a.totalAnswers);
      setModuleStats(modStats);
       if ((evalsRes.data?.answer_evaluations || []).length === 0) {
        try {
          const testStatsMap = {};
          const testResults = evalsRes.data?.test_results || [];
          testResults.forEach(r => {
            if (!testStatsMap[r.module_id]) {
              testStatsMap[r.module_id] = { total: 0, passed: 0 };
            }
            testStatsMap[r.module_id].total++;
            if (r.passed) testStatsMap[r.module_id].passed++;
          });
          
          const modStatsFromTests = (modulesRes.data || []).map(m => {
            const data = testStatsMap[m.id] || { total: 0, passed: 0 };
            return {
              id: m.id,
              title: m.title,
              category: m.category,
              totalAnswers: data.total,
              correctAnswers: data.passed,
              aiDetected: 0,
              pct: data.total > 0 ? Math.round((data.passed / data.total) * 100) : 0,
            };
          }).sort((a, b) => b.totalAnswers - a.totalAnswers);
          
          if (modStatsFromTests.some(m => m.totalAnswers > 0)) {
            setModuleStats(modStatsFromTests);
          }
        } catch (e) {
          console.warn('No se pudieron cargar test_results para moduleStats');
        }
      }



    } catch (err) {
      console.error(err);
      toast.error('Error al cargar datos del panel');
    } finally {
      setLoading(false);
    }
  }, [api, orgId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
  if (!orgId) return;
  let cancelled = false;
  api.get('/admin/users/course-activity', {
    params: empModuleFilter !== 'all' ? { module_id: empModuleFilter } : {},
  })
    .then(res => { if (!cancelled) setCourseActivity(res.data || {}); })
    .catch(() => { if (!cancelled) setCourseActivity({}); });
  return () => { cancelled = true; };
}, [api, orgId, empModuleFilter, users]);

  /* ── DEPT CRUD ── */
  const createDept = async () => {
    if (!newDept.name.trim()) return;
    try {
      await api.post('/admin/departments', { ...newDept, organization_id: orgId });
      toast.success('Departamento creado');
      setShowDeptModal(false);
      setNewDept({ name: '', description: '' });
      fetchAll();
    } catch { toast.error('Error al crear departamento'); }
  };
  const deleteDept = async (id) => {
    if (!window.confirm('¿Eliminar departamento?')) return;
    try {
      await api.delete(`/admin/departments/${id}`);
      toast.success('Eliminado');
      fetchAll();
    } catch { toast.error('Error'); }
  };

  /* ── ASSIGNMENT CRUD ── */
  const createAssignment = async () => {
  if (!newAssignment.module_id) return;
  try {
    await api.post('/admin/assignments', { ...newAssignment, organization_id: orgId });
    toast.success('Módulo asignado');
    setShowAssignModal(false);
    setNewAssignment({ module_id: '', assign_to: 'all', department_ids: [], user_ids: [], due_date: '', mandatory: true });
    fetchAll();
  } catch (e) {
    // El detail de FastAPI puede ser un objeto o string
    const detail = e.response?.data?.detail;
    const msg = typeof detail === 'string' 
      ? detail 
      : Array.isArray(detail) 
        ? detail.map(d => d.msg).join(', ')
        : 'Error al asignar';
    toast.error(msg);
  }
};
  const deleteAssignment = async (id) => {
    if (!window.confirm('¿Eliminar asignación?')) return;
    try {
      await api.delete(`/admin/assignments/${id}`);
      toast.success('Eliminada');
      fetchAll();
    } catch { toast.error('Error'); }
  };

const saveDueDate = async (a, valueOverride) => {
  const draft = getDueDraft(a);
  const value = valueOverride ?? draft.value;
  const unchanged = value === (a.due_date || '');
  if (unchanged && !draft.notify) return;

  setSavingDue(p => ({ ...p, [a.id]: true }));
  try {
    const res = await api.patch(`/admin/assignments/${a.id}/due-date`, {
      due_date: value || null,
      notify: draft.notify,
    });
    setAssignments(prev => prev.map(x => x.id === a.id ? { ...x, due_date: value || null } : x));
    setDueDrafts(p => { const n = { ...p }; delete n[a.id]; return n; });

    const base = unchanged ? 'Recordatorio' : value ? 'Fecha límite actualizada' : 'Fecha límite eliminada';
    if (draft.notify) {
      const { emails_sent, recipients, skipped_no_email } = res.data;
      const extra = skipped_no_email ? ` (${skipped_no_email} sin email real)` : '';
      toast.success(`${base} · ${emails_sent} de ${recipients} aviso(s) enviado(s)${extra}`);
    } else {
      toast.success(base);
    }
  } catch (e) {
    const detail = e.response?.data?.detail;
    toast.error(typeof detail === 'string' ? detail : 'Error al cambiar la fecha');
  } finally {
    setSavingDue(p => ({ ...p, [a.id]: false }));
  }
};

  const deleteModuleFromOrg = async (moduleId, title) => {
  if (!window.confirm(`¿Eliminar el módulo "${title}"? Se perderán todas sus unidades y evaluaciones.`)) return;
  try {
    await api.delete(`/admin/modules/${moduleId}`);
    toast.success('Módulo eliminado');
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al eliminar módulo');
  }
};

  const toggleDept = (id) => {
    setNewAssignment(p => ({
      ...p,
      department_ids: p.department_ids.includes(id)
        ? p.department_ids.filter(x => x !== id)
        : [...p.department_ids, id],
    }));
  };

  const uploadScorm = async () => {
    if (!scormFile) { toast.error('Selecciona un archivo ZIP'); return; }
    setUploadingScorm(true);
    setScormResult(null);
    try {
      const formData = new FormData();
      formData.append('file', scormFile);
      const res = await api.post('/superadmin/upload-scorm-with-units', formData);
      setScormResult(res.data);
      toast.success(`✅ "${res.data.parent_module_title}" — ${res.data.submodules_created} unidades · ${res.data.tests_created} tests`);
      setScormFile(null);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al procesar SCORM');
    } finally { setUploadingScorm(false); }
  };

  /* ── EMPLOYEE CRUD ── */
const openCreateEmp = () => {
  setEmpForm({ name: '', email: '', department_id: '', dni: '', active: true });
  setEditingEmp(null);
  setShowEmpModal(true);
};

const openEditEmp = (emp) => {
  setEmpForm({
    name: emp.name || '',
    email: emp.email || '',
    department_id: emp.department_id || emp.department || '',
    dni: emp.dni || '',
    active: emp.active !== false,
  });
  setEditingEmp(emp);
  setShowEmpModal(true);
};

const saveEmp = async () => {
  if (!empForm.name.trim() || !empForm.email.trim()) return;
  try {
    if (editingEmp) {
      await api.patch(`/admin/users/${editingEmp.id}`, { ...empForm, organization_id: orgId });
      toast.success('Empleado actualizado');
    } else {
      await api.post('/admin/users', { ...empForm, organization_id: orgId });
      toast.success('Empleado creado');
    }
    setShowEmpModal(false);
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al guardar empleado');
  }
};

const toggleEmpActive = async (emp) => {
  try {
    await api.patch(`/admin/users/${emp.id}`, { active: !emp.active });
    toast.success(emp.active ? 'Empleado desactivado' : 'Empleado activado');
    fetchAll();
  } catch { toast.error('Error al cambiar estado'); }
};

const deleteEmp = async (emp) => {
  if (!window.confirm(`¿Eliminar a ${emp.name}? Esta acción no se puede deshacer.`)) return;
  try {
    await api.delete(`/admin/users/${emp.id}`);
    toast.success('Empleado eliminado');
    fetchAll();
  } catch { toast.error('Error al eliminar'); }
};

const openEmpReport = async (emp) => {
  setShowEmpReport(true);
  setLoadingReport(true);
  setEmpReport(null);
  try {
    const res = await api.get(`/admin/users/${emp.id}/report`);
    setEmpReport(res.data);
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al cargar el informe');
    setShowEmpReport(false);
  } finally {
    setLoadingReport(false);
  }
};
const downloadModuleReport = async (moduleId, moduleTitle) => {
  setGeneratingReport(p => ({ ...p, [moduleId]: true }));
  try {
    const res = await api.get(`/admin/modules/${moduleId}/report.xlsx`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const safe = (moduleTitle || 'modulo').replaceAll(/[^a-z0-9]+/gi, '_').toLowerCase();
    const stamp = new Date().toISOString().slice(0, 10);
    const a = document.createElement('a');
    a.href = url;
    a.download = `informe_${safe}_${stamp}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Informe descargado');
  } catch (e) {
    console.error(e);
    toast.error('Error al generar el informe');
  } finally {
    setGeneratingReport(p => ({ ...p, [moduleId]: false }));
  }
};

const unassignModule = async (userId, moduleId) => {
  if (!window.confirm('¿Quitar este curso al empleado?')) return;
  try {
    await api.delete(`/admin/users/${userId}/assignments/${moduleId}`);
    toast.success('Curso desasignado');
    openEmpReport({ id: userId }); // refresca el informe
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al desasignar');
  }
};

/* ── BULK IMPORT ── */
const parseBulkFile = async (file) => {
  if (!file) return;
  setBulkFile(file);
  // Necesitas: npm install xlsx  (o importar SheetJS desde CDN si es artifact)
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
  // Normaliza cabeceras: nombre, apellido, email, departamento, dni
  const preview = rows.map((r, i) => ({
    _row: i + 2,
    name: `${r['nombre'] || r['name'] || ''} ${r['apellido'] || r['surname'] || ''}`.trim(),
    email: r['email'] || r['correo'] || '',
    department_name: r['departamento'] || r['department'] || '',
    dni: r['dni'] || '',
  })).filter(r => r.email);
  setBulkPreview(preview);
};

const confirmBulkImport = async () => {
  if (!bulkPreview.length) return;
  setBulkImporting(true);
  try {
    // Resuelve department_id por nombre
    const payload = bulkPreview.map(r => ({
      ...r,
      organization_id: orgId,
      active: true,
      department_id: departments.find(d =>
        d.name.toLowerCase() === r.department_name?.toLowerCase()
      )?.id || null,
    }));
    const res = await api.post('/admin/users/bulk', { users: payload });
    toast.success(`${res.data.created} empleados importados`);
    setShowBulkModal(false);
    setBulkPreview([]);
    setBulkFile(null);
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error en importación');
  } finally { setBulkImporting(false); }
};

const downloadTemplate = () => {
  // Genera un xlsx de plantilla descargable
  import('xlsx').then(XLSX => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['nombre', 'apellido', 'email', 'departamento', 'dni'],
      ['Ana', 'García', 'ana@empresa.com', 'Ventas', '12345678A'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Empleados');
    XLSX.writeFile(wb, 'plantilla_empleados.xlsx');
  });
};

/* ── SEND CODES ── */
const sendCodes = async () => {
  setSendingCode(true);
  try {
    const { type, id } = sendCodeTarget;
    if (type === 'all') {
      await api.post('/admin/users/send-all-codes', { organization_id: orgId });
      toast.success('Códigos enviados a todos los empleados pendientes');
    } else if (type === 'dept') {
      await api.post(`/admin/departments/${id}/send-codes`);
      toast.success('Códigos enviados al departamento');
    } else if (type === 'user') {
      await api.post(`/admin/users/${id}/send-code`);
      toast.success('Código enviado');
    }
    setShowSendCodeModal(false);
    fetchAll();
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al enviar códigos');
  } finally { setSendingCode(false); }
};

const remindNotStarted = async () => {
  if (empModuleFilter === 'all') return;
  const mod = modules.find(m => m.id === empModuleFilter);
  if (!window.confirm(
    `¿Enviar un recordatorio por correo a los ${notStartedCount} empleado(s) que no han empezado "${mod?.title}"?`
  )) return;

  setSendingReminder(true);
  try {
    const res = await api.post(`/admin/modules/${empModuleFilter}/remind-not-started`);
    const { emails_sent, recipients, skipped_no_email } = res.data;
    const extra = skipped_no_email ? ` (${skipped_no_email} sin email real)` : '';
    toast.success(`Recordatorio enviado a ${emails_sent} de ${recipients} empleado(s)${extra}`);
  } catch (e) {
    toast.error(e.response?.data?.detail || 'Error al enviar los recordatorios');
  } finally {
    setSendingReminder(false);
  }
};

  /* ── COMPUTED ── */
  const aiDetected = (evaluations.answer_evaluations || []).filter(e => e.is_ai_generated);
  const completedCount = stats?.completed_assignments || 0;
  const totalAssigned = stats?.total_user_assignments || stats?.total_assignments || 0;
  const completionRate = totalAssigned > 0 ? Math.round((completedCount / totalAssigned) * 100) : 0;
  const aiRate = stats?.total_roleplay_answers > 0 
    ? Math.round((stats.ai_detected_answers / stats.total_roleplay_answers) * 100) 
    : 0;
  /* ── TABS CONFIG — CHANGE 1: "Módulos" tab removed ── */
  const TABS = [
    { id: 'dashboard',   label: 'Dashboard',        icon: BarChart3 },
    { id: 'employees',   label: 'Empleados',         icon: Users },
    { id: 'assignments', label: 'Asignaciones',      icon: ClipboardList },
    { id: 'departments', label: 'Departamentos',     icon: Building2 },
    { id: 'scorm',       label: 'Subir SCORM',       icon: Upload },
    { id: 'ai-radar',   label: 'Radar IA',           icon: Shield },
    { id: 'courses',     label: 'Cursos',       icon: BookOpen },
    { id: 'settings', label: 'Ajustes', icon: UserCog },
  ];

  const getDeptId = (u) => u.department_id || u.department || '';
const getDeptName = (u) => departments.find(d => d.id === getDeptId(u))?.name || '';
const getInviteStatus = (u) => u.invite_status || (u.code_sent_at ? 'sent' : 'pending');
const getActivity = (u) => courseActivity[u.id]?.status || 'not_assigned';
const notStartedCount = users.filter(u => getActivity(u) === 'not_started').length;

const filteredUsers = useMemo(() => {
  const q = empSearch.trim().toLowerCase();
  const collator = new Intl.Collator('es', { sensitivity: 'base' });

  const list = users.filter(u => {
    if (q && !(
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.dni || '').toLowerCase().includes(q)
    )) return false;

    if (empDeptFilter === 'none' && getDeptId(u)) return false;
    if (empDeptFilter !== 'all' && empDeptFilter !== 'none' && getDeptId(u) !== empDeptFilter) return false;

    if (empStatusFilter !== 'all' && getActivity(u) !== empStatusFilter) return false;

    if (empCodeFilter !== 'all' && getInviteStatus(u) !== empCodeFilter) return false;
    return true;
  });

  const byName = (a, b) => collator.compare(a.name || '', b.name || '');

  list.sort((a, b) => {
    switch (empSort) {
      case 'name-desc': return byName(b, a);
      case 'email-asc': return collator.compare(a.email || '', b.email || '');
      case 'dept-asc': {
        const da = getDeptName(a), db = getDeptName(b);
        if (!da && db) return 1;          // sin departamento al final
        if (da && !db) return -1;
        return collator.compare(da, db) || byName(a, b);
      }
      case 'xp-desc': return (b.xp || 0) - (a.xp || 0) || byName(a, b);
      case 'name-asc':
      default: return byName(a, b);
    }
  });
  return list;
}, [users, departments, courseActivity, empSearch, empDeptFilter, empStatusFilter, empCodeFilter, empSort]);

const hasEmpFilters = empSearch || empModuleFilter !== 'all' || empDeptFilter !== 'all'
  || empStatusFilter !== 'all' || empCodeFilter !== 'all';
const clearEmpFilters = () => {
  setEmpSearch(''); setEmpModuleFilter('all'); setEmpDeptFilter('all');
  setEmpStatusFilter('all'); setEmpCodeFilter('all');
};

  if (loading) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="oa-root" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <Loader2 size={40} color="#00FFFF" style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }}
              className="animate-spin" />
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.2em', color: '#00FFFF', textTransform: 'uppercase' }}>
              Cargando panel...
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="oa-root">
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>

          {/* ── HEADER ── */}
          <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
  <div>
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', marginBottom: 6 }}>
      Panel de Administración
    </div>
    <h1 style={{ fontSize: 32, fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 4 }}>
      <span style={{ color: '#00FFFF', textShadow: '0 0 20px rgba(0,255,255,0.4)' }}>
        {orgData?.name || '...'}
      </span>
    </h1>
    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
      Gestiona módulos, empleados y monitoriza el progreso de tu organización
    </p>
  </div>

  <button className="oa-btn-ghost" onClick={() => setShowOrgDiploma(true)} style={{ borderColor: 'rgba(255,215,0,0.3)', color: '#FFD700' }}>
    <Award size={13} /> Diploma corporativo
  </button>
</div>

          {/* ── STAT CARDS ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 24 }}>
            <StatCard icon={Users} label="Empleados" value={users.length} color="#00FFFF" />
            <StatCard icon={BookOpen} label="Módulos" value={modules.length} color="#ccff00" />
            <StatCard icon={CheckCircle2} label="Completados" value={completedCount}
              color="#00FF88" sub={`${completionRate}% tasa`} />
            <StatCard icon={Brain} label="IA Detectada" value={stats?.ai_detected_answers || 0}
              color="#FF4444" sub={`${aiRate}% del total`} />
            <StatCard icon={Award} label="XP Total Org" value={users.reduce((s, u) => s + (u.xp || 0), 0)}
              color="#FF7300" />
          </div>

          {/* ── TABS ── */}
          <div className="oa-panel" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
              {TABS.map(t => (
                <button key={t.id} className={`oa-tab${tab === t.id ? ' active' : ''}`}
                  onClick={() => setTab(t.id)}>
                  <t.icon size={13} /> {t.label}
                </button>
              ))}
            </div>

            {/* ════════════════════════════════════════════════════
                TAB: DASHBOARD
            ════════════════════════════════════════════════════ */}
            {tab === 'dashboard' && (
              <div style={{ padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

                  {/* Completion Overview */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6 }}>
                          Progreso Global
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#fff' }}>
                          {completionRate}% completado
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
                          {completedCount} de {totalAssigned} asignaciones
                        </div>
                      </div>
                      <DonutMini pct={completionRate} color="#00FFFF" />
                    </div>

                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 12 }}>
                      Módulos más activos
                    </div>
                    {moduleStats.slice(0, 5).map(m => (
                      <MiniBar key={m.id} label={m.title} value={m.totalAnswers}
                        max={Math.max(...moduleStats.map(x => x.totalAnswers), 1)}
                        color="#00FFFF" sub=" resp." />
                    ))}
                    {moduleStats.length === 0 && (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '20px 0' }}>
                        Aún no hay respuestas registradas
                      </div>
                    )}
                  </div>

                  {/* AI Detection Chart */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6 }}>
                          Detección de IA
                        </div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: '#FF4444' }}>
                          {aiRate}% detectadas
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
  {stats?.ai_detected_answers || 0} de {stats?.total_roleplay_answers || 0} roleplays
</div>
                      </div>
                      <DonutMini pct={aiRate} color="#FF4444" />
                    </div>

                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 12 }}>
                      Módulos con más detecciones IA
                    </div>
                    {moduleStats.filter(m => m.aiDetected > 0).slice(0, 5).map(m => (
                      <MiniBar key={m.id} label={m.title} value={m.aiDetected}
                        max={Math.max(...moduleStats.map(x => x.aiDetected), 1)}
                        color="#FF4444" sub="" />
                    ))}
                    {moduleStats.filter(m => m.aiDetected > 0).length === 0 && (
                      <div style={{ fontSize: 12, color: '#00FF88', textAlign: 'center', padding: '20px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <CheckCircle2 size={16} /> Sin detecciones de IA
                      </div>
                    )}
                  </div>

                  {/* Correct Answer Rate per Module */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 16 }}>
                      Tasa de acierto por módulo
                    </div>
                    {moduleStats.slice(0, 6).map(m => (
                      <MiniBar key={m.id} label={m.title} value={m.pct}
                        max={100} color={m.pct >= 70 ? '#00FF88' : m.pct >= 40 ? '#ccff00' : '#FF4444'}
                        sub="%" />
                    ))}
                    {moduleStats.length === 0 && (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '20px 0' }}>
                        Sin datos aún
                      </div>
                    )}
                  </div>

                  {/* Top XP Ranking */}
                  <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 16 }}>
                      🏆 Ranking de empleados
                    </div>
                    {userProgress.slice(0, 6).map((u, i) => (
                      <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 5 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                          background: i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : 'rgba(255,255,255,0.08)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 10, fontWeight: 900, color: i < 3 ? '#000' : 'rgba(255,255,255,0.5)',
                        }}>{i + 1}</div>
                        <Avatar name={u.name} size={28} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Nivel {u.level}</div>
                        </div>
                        <div style={{ fontSize: 13, fontWeight: 900, color: '#FF7300', textShadow: '0 0 8px rgba(255,115,0,0.4)' }}>
                          {u.xp} XP
                        </div>
                      </div>
                    ))}
                    {userProgress.length === 0 && (
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '20px 0' }}>
                        Sin empleados aún
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════
                TAB: EMPLOYEES
            ════════════════════════════════════════════════════ */}
            {tab === 'employees' && (
  <div>
    {/* Subtabs + acciones */}
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap', gap: 10 }}>
  <div style={{ display: 'flex', gap: 6 }}>
    <button className={`oa-subtab${empSubTab === 'manage' ? ' active' : ''}`} onClick={() => setEmpSubTab('manage')}>Gestión</button>
    <button className={`oa-subtab${empSubTab === 'progress' ? ' active' : ''}`} onClick={() => setEmpSubTab('progress')}>Progreso</button>
  </div>

  <div style={{ display: 'flex', gap: 8 }}>
    <button
      className="oa-btn-ghost"
      onClick={() => { setSendCodeTarget({ type: 'all', id: null }); setShowSendCodeModal(true); }}
    >
      <Mail size={13} /> Enviar códigos
    </button>
    <button className="oa-btn-ghost" onClick={() => setShowBulkModal(true)}>
      <FileSpreadsheet size={13} /> Importar Excel
    </button>
    <button className="oa-btn-cyan" onClick={openCreateEmp}>
      <UserPlus size={13} /> Nuevo empleado
    </button>
  </div>
</div>
{/* SUBTAB: GESTIÓN */}
    {empSubTab === 'manage' && (
  <div>
    {/* ── Barra de filtros ── */}
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 200 }}>
        <Search size={13} color="rgba(255,255,255,0.35)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          className="oa-input"
          style={{ paddingLeft: 34 }}
          placeholder="Buscar nombre, email o DNI..."
          value={empSearch}
          onChange={e => setEmpSearch(e.target.value)}
        />
      </div>
      <select className="oa-input" style={{ width: 'auto', flex: '0 1 220px' }}
  value={empModuleFilter} onChange={e => setEmpModuleFilter(e.target.value)}>
  <option value="all">Todos los cursos</option>
  {modules.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
</select>

      <select className="oa-input" style={{ width: 'auto', flex: '0 1 170px' }} value={empDeptFilter} onChange={e => setEmpDeptFilter(e.target.value)}>
        <option value="all">Todos los departamentos</option>
        <option value="none">Sin departamento</option>
        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>

      <select className="oa-input" style={{ width: 'auto', flex: '0 1 190px' }}
  value={empStatusFilter} onChange={e => setEmpStatusFilter(e.target.value)}>
  <option value="all">Cualquier estado</option>
  <option value="not_started">Sin empezar ({notStartedCount})</option>
  <option value="started">En curso</option>
  <option value="completed">Completados</option>
  <option value="not_assigned">{empModuleFilter === 'all' ? 'Sin curso asignado' : 'No asignados a este curso'}</option>
</select>

      <select className="oa-input" style={{ width: 'auto', flex: '0 1 150px' }} value={empCodeFilter} onChange={e => setEmpCodeFilter(e.target.value)}>
        <option value="all">Cualquier código</option>
        <option value="pending">Sin enviar</option>
        <option value="sent">Enviado</option>
        <option value="registered">Registrado</option>
      </select>

      <select className="oa-input" style={{ width: 'auto', flex: '0 1 170px' }} value={empSort} onChange={e => setEmpSort(e.target.value)}>
        <option value="name-asc">Nombre A → Z</option>
        <option value="name-desc">Nombre Z → A</option>
        <option value="dept-asc">Departamento</option>
        <option value="email-asc">Email A → Z</option>
        <option value="xp-desc">Más XP</option>
      </select>
{empModuleFilter !== 'all' && notStartedCount > 0 && (
  <button className="oa-btn-ghost" onClick={remindNotStarted} disabled={sendingReminder}
    style={{ color: '#FFA500', borderColor: 'rgba(255,165,0,0.35)' }}
    title="Enviar recordatorio por correo a los que no han empezado este curso">
    {sendingReminder
      ? <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Enviando...</>
      : <><Mail size={12} /> Recordar a los sin empezar ({notStartedCount})</>}
  </button>
)}
      {hasEmpFilters && (
        <button className="oa-btn-ghost" onClick={clearEmpFilters}><X size={12} /> Limpiar</button>
      )}
      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
        {filteredUsers.length} de {users.length}
      </span>
    </div>

    <div className="oa-emp-header">
      <span>Empleado</span><span>Email</span><span>Departamento</span>
      <span>DNI</span><span>Código</span><span>Acciones</span>
    </div>

    <div className="oa-scroll" style={{ maxHeight: 480, overflowY: 'auto' }}>
      {filteredUsers.map(u => {
        const inviteStatus = getInviteStatus(u);
        return (
          <div key={u.id} className="oa-emp-grid" style={{
  borderBottom: '1px solid rgba(255,255,255,0.04)',
  opacity: u.active === false ? 0.45 : 1,
}}>
  {/* Empleado */}
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    <Avatar name={u.name} size={30} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
        {(() => {
          const st = getActivity(u);
          const map = {
            not_started:  ['#FF7300', 'Sin empezar'],
            started:      ['#ccff00', 'En curso'],
            completed:    ['#00FF88', 'Completado'],
            not_assigned: ['rgba(255,255,255,0.4)', 'Sin curso'],
          };
          const [color, label] = map[st];
          return <Badge color={color}>{label}</Badge>;
        })()}
        {u.active === false && (
          <span style={{ fontSize: 9, color: '#FF4444', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Inactivo</span>
        )}
      </div>
    </div>
  </div>

  {/* Email */}
  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>

  {/* Departamento */}
  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{getDeptName(u) || '—'}</div>

  {/* DNI */}
  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.dni || '—'}>
    {u.dni || '—'}
  </div>

  {/* Código */}
  <div>
    {inviteStatus === 'registered'
      ? <span className="oa-code-btn registered"><MailCheck size={10} /> Registrado</span>
      : inviteStatus === 'sent'
        ? <button className="oa-code-btn sent" onClick={() => { setSendCodeTarget({ type: 'user', id: u.id }); setShowSendCodeModal(true); }}>
            <Mail size={10} /> Reenviar
          </button>
        : <button className="oa-code-btn pending" onClick={() => { setSendCodeTarget({ type: 'user', id: u.id }); setShowSendCodeModal(true); }}>
            <Mail size={10} /> Enviar código
          </button>}
  </div>

  {/* Acciones */}
  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
    <button className={`oa-toggle ${u.active !== false ? 'on' : 'off'}`} onClick={() => toggleEmpActive(u)} title={u.active !== false ? 'Desactivar' : 'Activar'} />
    <button className="oa-btn-ghost" onClick={() => openEditEmp(u)} style={{ padding: '6px 8px' }} title="Editar"><UserCog size={13} /></button>
    <button className="oa-btn-ghost" onClick={() => openEmpReport(u)} style={{ padding: '6px 8px' }} title="Ver informe"><Eye size={13} /></button>
    <button className="oa-btn-danger" onClick={() => deleteEmp(u)} title="Eliminar"><Trash2 size={13} /></button>
  </div>
</div>
        );
      })}

      {filteredUsers.length === 0 && (
        <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
          {users.length === 0
            ? 'No hay empleados. Añade uno o importa desde Excel.'
            : 'Ningún empleado coincide con los filtros.'}
        </div>
      )}
    </div>
  </div>
)}

    {/* SUBTAB: PROGRESO */}
    {empSubTab === 'progress' && (
      <div>
        <div className="oa-table-header">
          <span>Empleado</span><span>XP</span><span>Respuestas</span><span>Aciertos</span><span>Estado IA</span>
        </div>
        <div className="oa-scroll" style={{ maxHeight: 500, overflowY: 'auto' }}>
          {userProgress.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
              No hay datos de progreso aún.
            </div>
          ) : (
            userProgress.map(u => {
              const aciertos = u.eval.total > 0 ? Math.round((u.eval.correct / u.eval.total) * 100) : null;
              const dept = departments.find(d => d.id === u.department || d.name === u.department);
              return (
                <div key={u.id} className="oa-table-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={u.name} size={30} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                      {dept && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{dept.name}</div>}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#FF7300' }}>{u.xp}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>{u.eval.total}</div>
                  <div>
                    {aciertos !== null
                      ? <span style={{ fontSize: 13, fontWeight: 700, color: aciertos >= 70 ? '#00FF88' : aciertos >= 40 ? '#ccff00' : '#FF4444' }}>{aciertos}%</span>
                      : <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12 }}>—</span>}
                  </div>
                  <div>
                    {u.eval.ai > 0
                      ? <Badge color="#FF4444">⚠ {u.eval.ai} detectadas</Badge>
                      : <Badge color="#00FF88">✓ Limpio</Badge>}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    )}
  </div>
)}

            {/* ════════════════════════════════════════════════════
                TAB: ASSIGNMENTS
                CHANGE 2: Modal select now shows real modules list
            ════════════════════════════════════════════════════ */}
            {tab === 'assignments' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px 0' }}>
                  <button className="oa-btn-cyan" onClick={() => {
  setNewAssignment({ 
    module_id: '', 
    assign_to: 'all', 
    department_ids: [], 
    user_ids: [], 
    due_date: '', 
    mandatory: true 
  });
  setShowAssignModal(true);
}}>
                    <Send size={13} /> Asignar Módulo
                  </button>
                </div>
                {assignments.map(a => {
  const mod = modules.find(m => m.id === a.module_id) || {};
  const settings = moduleSettings[a.module_id] || {};
  const recOn  = settings.require_screen_recording ?? mod.requires_screen_record ?? false;
  const timeOn = settings.require_time_tracking    ?? mod.require_time_tracking  ?? false;
  const hours  = settings.hours ?? mod.hours ?? 0;

  return (
    <div key={a.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      {/* Fila principal */}
      <div className="oa-row" style={{ borderBottom: 'none' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{a.module_title}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Badge color="rgba(255,255,255,0.5)">
              {a.assign_to === 'all' ? (
  <Badge color="rgba(255,255,255,0.5)">Todos</Badge>
) : (
  <>
    {(a.department_ids || []).length > 0 && (
      <Badge color="rgba(255,255,255,0.5)">{a.department_ids.length} depts</Badge>
    )}
    {(a.user_ids || []).length > 0 && (
      <Badge color="rgba(255,255,255,0.5)">{a.user_ids.length} usuarios</Badge>
    )}
  </>
)}
            </Badge>
            {a.mandatory && <Badge color="#FF7300">Obligatorio</Badge>}
            {a.due_date && <Badge color="#ccff00">Límite: {a.due_date}</Badge>}
          </div>
        </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button
            className="oa-btn-ghost"
            onClick={() => openEditModuleName(a.module_id, a.module_title)}
            style={{ padding: '7px 12px', fontSize: 10, color: '#00FFFF', borderColor: 'rgba(0,255,255,0.3)' }}
            title="Renombrar módulo"
          >
            <Edit size={12} />
          </button>
          <button
            className="oa-btn-ghost"
            onClick={() => downloadModuleReport(a.module_id, a.module_title)}
            disabled={generatingReport[a.module_id]}
            style={{ padding: '7px 12px', fontSize: 10, color: '#ccff00', borderColor: 'rgba(204,255,0,0.3)' }}
            title="Descargar informe del módulo"
          >
            {generatingReport[a.module_id]
              ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
              : <Download size={12} />}
          </button>
          <button className="oa-btn-danger" onClick={() => deleteAssignment(a.id)} title="Quitar asignación">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Configuración del módulo */}
      <div style={{
        display: 'flex', gap: 12, padding: '10px 20px 14px',
        background: 'rgba(255,255,255,0.01)', flexWrap: 'wrap',
      }}>
        {/* Grabación */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 14px', borderRadius: 10,
          background: recOn ? 'rgba(0,255,255,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${recOn ? 'rgba(0,255,255,0.2)' : 'rgba(255,255,255,0.06)'}`,
          transition: 'all 0.2s',
        }}>
          <Eye size={13} color={recOn ? '#00FFFF' : 'rgba(255,255,255,0.3)'} />
          <span style={{ fontSize: 11, fontWeight: 700, color: recOn ? '#00FFFF' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Grabación pantalla
          </span>
          {savingModuleSettings[`${a.module_id}-require_screen_recording`] && (
            <Loader2 size={11} color="rgba(255,255,255,0.4)" className="animate-spin" />
          )}
          <button
            className={`oa-toggle ${recOn ? 'on' : 'off'}`}
            style={{ width: 32, height: 18 }}
            onClick={() => updateModuleSetting(a.module_id, 'require_screen_recording', !recOn)}
            disabled={savingModuleSettings[`${a.module_id}-require_screen_recording`]}
          />
        </div>

        {/* Tiempo mínimo */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 14px', borderRadius: 10,
          background: timeOn ? 'rgba(204,255,0,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${timeOn ? 'rgba(204,255,0,0.2)' : 'rgba(255,255,255,0.06)'}`,
          transition: 'all 0.2s',
        }}>
          <Clock size={13} color={timeOn ? '#ccff00' : 'rgba(255,255,255,0.3)'} />
          <span style={{ fontSize: 11, fontWeight: 700, color: timeOn ? '#ccff00' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Tiempo mínimo
          </span>
          {timeOn && (
            <input
              type="number" min="1" max="999"
              value={hours || 1}
              onChange={e => setModuleSettings(p => ({ ...p, [a.module_id]: { ...p[a.module_id], hours: Number(e.target.value) } }))}
              onBlur={e => updateModuleSetting(a.module_id, 'hours', Number(e.target.value))}
              style={{ width: 48, padding: '3px 6px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(204,255,0,0.3)', borderRadius: 6, color: '#ccff00', fontSize: 12, fontWeight: 700, textAlign: 'center' }}
            />
          )}
          {timeOn && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>h</span>}
          {savingModuleSettings[`${a.module_id}-require_time_tracking`] && (
            <Loader2 size={11} color="rgba(255,255,255,0.4)" className="animate-spin" />
          )}
          <button
            className={`oa-toggle ${timeOn ? 'on' : 'off'}`}
            style={{ width: 32, height: 18 }}
            onClick={() => updateModuleSetting(a.module_id, 'require_time_tracking', !timeOn)}
            disabled={savingModuleSettings[`${a.module_id}-require_time_tracking`]}
          />
        </div>

        {/* Fecha límite */}
{(() => {
  const draft = getDueDraft(a);
  const dirty = draft.value !== (a.due_date || '');
  const saving = savingDue[a.id];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '8px 14px', borderRadius: 10,
      background: a.due_date ? 'rgba(204,255,0,0.06)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${a.due_date ? 'rgba(204,255,0,0.2)' : 'rgba(255,255,255,0.06)'}`,
      transition: 'all 0.2s',
    }}>
      <Calendar size={13} color={a.due_date ? '#ccff00' : 'rgba(255,255,255,0.3)'} />
      <span style={{ fontSize: 11, fontWeight: 700, color: a.due_date ? '#ccff00' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Fecha límite
      </span>
      <input
        type="date"
        value={draft.value}
        onChange={e => setDueDraft(a, { value: e.target.value })}
        style={{
          padding: '3px 6px', background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(204,255,0,0.3)', borderRadius: 6,
          color: '#ccff00', fontSize: 12, fontWeight: 700, colorScheme: 'dark',
        }}
      />
      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        <input
          type="checkbox"
          checked={draft.notify}
          onChange={e => setDueDraft(a, { notify: e.target.checked })}
          style={{ accentColor: '#ccff00' }}
        />
        Avisar
      </label>
      {(dirty || draft.notify) && (
        <button
          onClick={() => saveDueDate(a)}
          disabled={saving}
          style={{
            background: '#ccff00', color: '#000', border: 'none', borderRadius: 6,
            padding: '4px 10px', fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
            textTransform: 'uppercase', cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1, fontFamily: 'DM Sans, sans-serif',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {saving && <Loader2 size={10} className="animate-spin" />} {dirty ? 'Guardar' : 'Enviar aviso'}
        </button>
      )}
      {a.due_date && !dirty && !draft.notify && (
        <button
          onClick={() => saveDueDate(a, '')}
          disabled={saving}
          title="Quitar fecha límite"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex', padding: 0 }}
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
})()}

        {/* Diploma */}
<div style={{
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '8px 14px', borderRadius: 10,
  background: (moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false)
    ? 'rgba(255,215,0,0.06)' : 'rgba(255,255,255,0.03)',
  border: `1px solid ${(moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false)
    ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.06)'}`,
  transition: 'all 0.2s',
}}>
  <Award size={13} color={(moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false) ? '#FFD700' : 'rgba(255,255,255,0.3)'} />
  <span style={{ fontSize: 11, fontWeight: 700, color: (moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false) ? '#FFD700' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
    Diploma al completar
  </span>
  {savingModuleSettings[`${a.module_id}-diploma_enabled`] && (
    <Loader2 size={11} color="rgba(255,255,255,0.4)" className="animate-spin" />
  )}
  <button
    className={`oa-toggle ${(moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false) ? 'on' : 'off'}`}
    style={{ width: 32, height: 18 }}
    onClick={() => updateModuleSetting(
      a.module_id, 'diploma_enabled',
      !(moduleSettings[a.module_id]?.diploma_enabled ?? mod.diploma_enabled ?? false)
    )}
    disabled={savingModuleSettings[`${a.module_id}-diploma_enabled`]}
  />
  <button
  onClick={() => openDiplomaConfig(
    a.module_id,
    a.module_title,
    moduleSettings[a.module_id]?.diplomaConfig ?? mod.diplomaConfig ?? null
  )}
  title="Personalizar diploma"
  style={{
    background: 'rgba(255,215,0,0.08)',
    border: '1px solid rgba(255,215,0,0.25)',
    borderRadius: 7,
    padding: '4px 8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: '#FFD700',
    fontSize: 10,
    fontWeight: 800,
    fontFamily: 'DM Sans, sans-serif',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    marginRight: 4,
  }}
>
  <Edit size={11} /> Diseño
</button>

</div>
      </div>
      {/* Test propio de la plataforma (en vez del SCORM interno) */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 14px', borderRadius: 10,
          background: (moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false)
            ? 'rgba(255,0,255,0.06)' : 'rgba(255,255,255,0.03)',
          border: `1px solid ${(moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false)
            ? 'rgba(255,0,255,0.2)' : 'rgba(255,255,255,0.06)'}`,
          transition: 'all 0.2s',
        }}>
          <FileText size={13} color={(moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false) ? '#FF00FF' : 'rgba(255,255,255,0.3)'} />
          <span style={{ fontSize: 11, fontWeight: 700, color: (moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false) ? '#FF00FF' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Test de plataforma (SCORM sin test propio)
          </span>
          {savingModuleSettings[`${a.module_id}-use_platform_test`] && (
            <Loader2 size={11} color="rgba(255,255,255,0.4)" className="animate-spin" />
          )}
          <button
            className={`oa-toggle ${(moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false) ? 'on' : 'off'}`}
            style={{ width: 32, height: 18 }}
            onClick={() => updateModuleSetting(
              a.module_id, 'use_platform_test',
              !(moduleSettings[a.module_id]?.use_platform_test ?? mod.use_platform_test ?? false)
            )}
            disabled={savingModuleSettings[`${a.module_id}-use_platform_test`]}
          />
        </div>
    </div>
    
  );
  
  
})}
                {assignments.length === 0 && (
                  <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                    No hay asignaciones. Asigna módulos a tus empleados.
                  </div>
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════════════
                TAB: DEPARTMENTS
            ════════════════════════════════════════════════════ */}
            {tab === 'departments' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px 0' }}>
                  <button className="oa-btn-cyan" onClick={() => setShowDeptModal(true)}>
                    <Plus size={13} /> Nuevo Departamento
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, padding: 20 }}>
                  {departments.map(d => (
                    <div key={d.id} style={{
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 14, padding: 16,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building2 size={16} color="#00FFFF" />
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{d.name}</span>
                        </div>
                        <button className="oa-btn-danger" onClick={() => deleteDept(d.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                      {d.description && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>{d.description}</p>}
                      <Badge color="#00FFFF">{d.user_count || 0} empleados</Badge>
                    </div>
                  ))}
                  {departments.length === 0 && (
                    <div style={{ gridColumn: '1/-1', padding: '32px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                      No hay departamentos. Crea uno para organizar a tu equipo.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════
                TAB: SCORM
            ════════════════════════════════════════════════════ */}
            {tab === 'scorm' && (
              <div style={{ padding: 28, maxWidth: 600, margin: '0 auto' }}>
                <h3 style={{ fontSize: 16, fontWeight: 900, color: '#fff', marginBottom: 8 }}>Subir Paquete SCORM</h3>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 20, lineHeight: 1.6 }}>
                  Sube un ZIP maestro con los ZIPs de cada unidad (UNIDAD_1.zip, UNIDAD_2.zip...) y los PDFs de respuestas.
                </p>

                <label style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', border: '2px dashed rgba(255,0,255,0.3)', borderRadius: 16, cursor: 'pointer', background: 'rgba(255,0,255,0.03)', marginBottom: 16 }}>
                  <div style={{ padding: 12, background: 'rgba(255,0,255,0.1)', borderRadius: 10, color: '#FF00FF', flexShrink: 0 }}>
                    <PackageOpen size={22} />
                  </div>
                  <div style={{ flex: 1 }}>
                    {scormFile ? (
                      <>
                        <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{scormFile.name}</p>
                        <p style={{ fontSize: 11, color: '#FF00FF', fontWeight: 800 }}>{(scormFile.size / 1024 / 1024).toFixed(2)} MB listo</p>
                      </>
                    ) : (
                      <>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Seleccionar archivo ZIP</p>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Paquete SCORM con sub-unidades</p>
                      </>
                    )}
                  </div>
                  <input type="file" accept=".zip" style={{ display: 'none' }} onChange={e => { setScormFile(e.target.files[0] || null); setScormResult(null); }} />
                </label>

                <button
                  onClick={uploadScorm}
                  disabled={uploadingScorm || !scormFile}
                  style={{ width: '100%', justifyContent: 'center', background: '#FF00FF', color: '#fff', fontWeight: 800, border: 'none', borderRadius: 10, padding: '12px 20px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: uploadingScorm || !scormFile ? 'not-allowed' : 'pointer', opacity: uploadingScorm || !scormFile ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {uploadingScorm ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Procesando (1-3 min)...</> : <><Upload size={14} /> Iniciar Carga SCORM</>}
                </button>

                {scormResult && (
                  <div style={{ marginTop: 20, padding: 20, background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <CheckCircle2 size={20} color="#00FF88" />
                      <span style={{ fontSize: 14, fontWeight: 800, color: '#00FF88' }}>"{scormResult.parent_module_title}" importado</span>
                    </div>
                    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{scormResult.submodules_created} unidades · {scormResult.tests_created} evaluaciones creadas</p>
                  </div>
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════════════
                TAB: AI RADAR
            ════════════════════════════════════════════════════ */}
            {tab === 'ai-radar' && (() => {
  // Leer de roleplay_evaluations en vez de answer_evaluations
  const allRoleplayEvals = evaluations.roleplay_evaluations || [];
  const aiDetected = allRoleplayEvals.filter(e => e.is_ai_generated || e.fraud_detected);
  const aiRate = allRoleplayEvals.length > 0
    ? Math.round((aiDetected.length / allRoleplayEvals.length) * 100)
    : 0;
  const totalRoleplays = allRoleplayEvals.length;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Detección overview */}
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 6 }}>
                Detección en Roleplays
              </div>
              <div style={{ fontSize: 22, fontWeight: 900, color: '#FF4444' }}>
                {aiRate}% detectadas
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
                {aiDetected.length} de {totalRoleplays} roleplays
              </div>
            </div>
            <DonutMini pct={aiRate} color="#FF4444" />
          </div>

          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', marginBottom: 12 }}>
            Por módulo
          </div>
          {modules.map(m => {
            const mEvals = allRoleplayEvals.filter(e => e.module_id === m.id);
            const mAi = mEvals.filter(e => e.is_ai_generated || e.fraud_detected).length;
            if (!mEvals.length) return null;
            return (
              <MiniBar key={m.id} label={m.title} value={mAi}
                max={Math.max(...modules.map(x => 
                  allRoleplayEvals.filter(e => e.module_id === x.id && (e.is_ai_generated || e.fraud_detected)).length
                ), 1)} color="#FF4444" />
            );
          })}
          {!totalRoleplays && (
            <div style={{ color: '#00FF88', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
              <CheckCircle2 size={16} /> Sin roleplays enviados aún
            </div>
          )}
        </div>

        {/* Empleados con detecciones */}
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 16, padding: 20, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 16 }}>
            Empleados con detecciones
          </div>
          {users
            .map(u => ({
              ...u,
              detections: aiDetected.filter(e => e.user_id === u.id),
              total: allRoleplayEvals.filter(e => e.user_id === u.id).length,
            }))
            .filter(u => u.detections.length > 0)
            .sort((a, b) => b.detections.length - a.detections.length)
            .map(u => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <Avatar name={u.name} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.name}
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                    {u.total} roleplays totales
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#FF4444' }}>{u.detections.length}</div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>detectadas</div>
                </div>
              </div>
            ))
          }
          {!aiDetected.length && (
            <div style={{ color: '#00FF88', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
              <Shield size={16} /> Ningún empleado con detecciones
            </div>
          )}
        </div>
      </div>

      {/* Historial detallado */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: 14 }}>
          Historial de detecciones
        </div>
        <div className="oa-scroll" style={{ maxHeight: 320, overflowY: 'auto' }}>
          {aiDetected.slice(0, 20).map(e => {
            const u = users.find(u => u.id === e.user_id);
            const m = modules.find(m => m.id === e.module_id);
            return (
              <div key={e.id} className="oa-ai-item">
                <Avatar name={u?.name || '?'} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>
                    {u?.name || 'Usuario desconocido'}
                  </div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m?.title || 'Módulo'} · {new Date(e.evaluated_at).toLocaleDateString('es-ES')}
                  </div>
                  {/* Mostrar el paso con mayor probabilidad */}
                  {e.responses?.find(r => r.is_ai_generated) && (
                    <div style={{ fontSize: 10, color: 'rgba(255,100,100,0.7)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      "{e.responses.find(r => r.is_ai_generated)?.user_response?.slice(0, 80)}..."
                    </div>
                  )}
                </div>
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#FF4444' }}>
                    {Math.round(e.overall_ai_probability)}%
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>prob. IA</div>
                </div>
                <Badge color={e.fraud_reason === 'paste' ? '#FF7300' : '#FF4444'}>
                  {e.fraud_reason === 'paste' ? 'Copy-paste' : 'IA detectada'}
                </Badge>
              </div>
            );
          })}
          {!aiDetected.length && (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#00FF88', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <CheckCircle2 size={16} /> Sin detecciones de IA en roleplays
            </div>
          )}
        </div>
      </div>
    </div>
  );
})()}
{/* ════════ TAB: CURSOS ════════ */}
{tab === 'courses' && (
  <div>
    <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
        Todos los cursos ({modules.length})
      </div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
        Eliminar un curso lo borra por completo, incluidas sus asignaciones.
      </div>
    </div>

    {modules.map(m => {
      const isAssigned = assignments.some(a => a.module_id === m.id);
      return (
        <div key={m.id} className="oa-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{m.title}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {m.category && <Badge color="rgba(255,255,255,0.5)">{m.category}</Badge>}
              {isAssigned
                ? <Badge color="#00FF88">Asignado</Badge>
                : <Badge color="#FFA500">Sin asignar</Badge>}
            </div>
          </div>
          <button
            className="oa-btn-danger"
            onClick={() => deleteModuleFromOrg(m.id, m.title)}
            title="Eliminar curso por completo"
          >
            <Trash2 size={14} />
          </button>
        </div>
      );
    })}

    {modules.length === 0 && (
      <div style={{ padding: '48px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
        No hay cursos. Sube un SCORM para crear el primero.
      </div>
    )}
  </div>
)}
{tab === 'settings' && (
  <div style={{ padding: 28, maxWidth: 640, margin: '0 auto' }}>
    <h3 style={{ fontSize: 16, fontWeight: 900, color: '#fff', marginBottom: 6 }}>
      "Tu Círculo" de los empleados
    </h3>
    <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 20, lineHeight: 1.6 }}>
      Elige a quién ven tus empleados en su círculo del dashboard.
    </p>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {[
        { v: 'all',        l: 'Todos',                sub: 'Ven a compañeros de toda la empresa' },
        { v: 'department', l: 'Solo mi departamento', sub: 'Cada empleado ve únicamente a los de su departamento' },
        { v: 'none',       l: 'Nadie',                sub: 'El círculo se oculta por completo' },
      ].map(o => {
        const active = (orgData?.circle_mode || 'all') === o.v;
        return (
          <button key={o.v} type="button" disabled={savingCircle}
            onClick={() => saveCircleMode(o.v)}
            aria-pressed={active}
            style={{
              width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit',
              padding: '14px 16px', borderRadius: 12, cursor: savingCircle ? 'not-allowed' : 'pointer',
              border: `1px solid ${active ? '#00FFFF' : 'rgba(255,255,255,0.08)'}`,
              background: active ? 'rgba(0,255,255,0.08)' : 'rgba(255,255,255,0.02)',
              transition: 'all 0.2s',
            }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: active ? '#00FFFF' : '#fff' }}>
              {o.l}
            </span>
            <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
              {o.sub}
            </span>
          </button>
        );
      })}
    </div>
  </div>
)}
          </div>{/* end panel */}
        </div>{/* end maxWidth */}
      </div>{/* end root */}

      {/* ════════════════════════════════════════════════════
          MODAL: ASSIGN MODULE
          CHANGE 2: select renders real modules from state
      ════════════════════════════════════════════════════ */}
      {showAssignModal && (
        <ModalOverlay className="oa-modal-overlay" label="Asignar módulo" onClose={() => setShowAssignModal(false)}>
          <div className="oa-modal">
            <div className="oa-modal-header">
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Asignar Módulo</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
                  Selecciona módulo y destinatarios
                </div>
              </div>
              <button className="oa-btn-ghost" onClick={() => setShowAssignModal(false)} style={{ padding: '8px' }}>
                <X size={16} />
              </button>
            </div>
            <div className="oa-modal-body oa-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
                  Módulo * {modules.length === 0 && <span style={{ color: '#FF4444', fontWeight: 700 }}>(sin módulos disponibles)</span>}
                </div>
                <select
                  className="oa-input"
                  value={newAssignment.module_id}
                  onChange={e => setNewAssignment(p => ({ ...p, module_id: e.target.value }))}
                >
                  <option value="">— Selecciona un módulo —</option>
                  {modules.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.title}{m.category ? ` · ${m.category}` : ''}{m.duration_minutes ? ` · ${m.duration_minutes} min` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Asignar a</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {[{ v: 'all', l: 'Todos' }, { v: 'department', l: 'Por Dept.' }, { v: 'users', l: 'Usuarios' }].map(o => (
                    <label key={o.v} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 4, padding: '12px 8px', border: `1px solid ${newAssignment.assign_to === o.v ? '#00FFFF' : 'rgba(255,255,255,0.08)'}`,
                      borderRadius: 10, cursor: 'pointer', fontSize: 11, fontWeight: 700, textAlign: 'center',
                      background: newAssignment.assign_to === o.v ? 'rgba(0,255,255,0.08)' : 'rgba(255,255,255,0.02)',
                      color: newAssignment.assign_to === o.v ? '#00FFFF' : 'rgba(255,255,255,0.5)',
                      transition: 'all 0.2s',
                    }}>
                      <input type="radio" name="assignTo" value={o.v} style={{ display: 'none' }}
                        checked={newAssignment.assign_to === o.v}
                        onChange={() => setNewAssignment(p => ({ ...p, assign_to: o.v, department_ids: [], user_ids: [] }))} />
                      {o.l}
                    </label>
                  ))}
                </div>
              </div>

              {newAssignment.assign_to === 'department' && departments.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Departamentos</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto' }}>
                    {departments.map(d => (
                      <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={newAssignment.department_ids.includes(d.id)} onChange={() => toggleDept(d.id)}
                          style={{ accentColor: '#00FFFF' }} />
                        <span style={{ fontSize: 13, color: '#fff' }}>{d.name}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{d.user_count} empleados</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {newAssignment.assign_to === 'users' && users.length > 0 && (
  <div>
    <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
      Usuarios ({newAssignment.user_ids.length} seleccionados)
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }} className="oa-scroll">
      {users.map(u => (
        <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={newAssignment.user_ids.includes(u.id)}
            onChange={() => setNewAssignment(p => ({
              ...p,
              user_ids: p.user_ids.includes(u.id)
                ? p.user_ids.filter(x => x !== u.id)
                : [...p.user_ids, u.id],
            }))}
            style={{ accentColor: '#00FFFF' }}
          />
          <Avatar name={u.name} size={26} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{u.name}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{u.email}</div>
          </div>
          {departments.find(d => d.id === (u.department_id || u.department)) && (
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
              {departments.find(d => d.id === (u.department_id || u.department))?.name}
            </span>
          )}
        </label>
      ))}
    </div>
  </div>
)}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Fecha límite (opcional)</div>
                  <input type="date" className="oa-input" value={newAssignment.due_date}
                    onChange={e => setNewAssignment(p => ({ ...p, due_date: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 2 }}>
  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
    <div style={{
      width: 20, height: 20, borderRadius: 6, border: `1px solid ${newAssignment.mandatory ? '#00FFFF' : 'rgba(255,255,255,0.2)'}`,
      background: newAssignment.mandatory ? '#00FFFF' : 'transparent',
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
    }}>
      {newAssignment.mandatory && <span style={{ fontSize: 12, fontWeight: 900, color: '#000' }}>✓</span>}
    </div>
    <input type="checkbox" style={{ display: 'none' }} checked={newAssignment.mandatory}
      onChange={e => setNewAssignment(p => ({ ...p, mandatory: e.target.checked }))} />
    <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Obligatorio</span>
  </label>
</div>
              </div>

            </div>
            <div className="oa-modal-footer">
              <button className="oa-btn-ghost" onClick={() => setShowAssignModal(false)}>Cancelar</button>
<button className="oa-btn-cyan" onClick={createAssignment} disabled={
  !newAssignment.module_id ||
  (newAssignment.assign_to === 'users' && newAssignment.user_ids.length === 0) ||
  (newAssignment.assign_to === 'department' && newAssignment.department_ids.length === 0)
}>                <Send size={13} /> Asignar
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ════════════════════════════════════════════════════
          MODAL: CREATE DEPARTMENT
      ════════════════════════════════════════════════════ */}
      {showDeptModal && (
        <ModalOverlay className="oa-modal-overlay" label="Nuevo departamento" onClose={() => setShowDeptModal(false)}>
          <div className="oa-modal" style={{ maxWidth: 440 }}>
            <div className="oa-modal-header">
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Nuevo Departamento</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
                  Organiza tu equipo por áreas
                </div>
              </div>
              <button className="oa-btn-ghost" onClick={() => setShowDeptModal(false)} style={{ padding: '8px' }}>
                <X size={16} />
              </button>
            </div>
            <div className="oa-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Nombre *</div>
                <input className="oa-input" placeholder="Ej: Ventas, Marketing, IT..." value={newDept.name}
                  onChange={e => setNewDept(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Descripción (opcional)</div>
                <textarea className="oa-input" placeholder="Descripción del departamento..." rows={3}
                  value={newDept.description}
                  onChange={e => setNewDept(p => ({ ...p, description: e.target.value }))}
                  style={{ resize: 'none' }} />
              </div>
            </div>
            <div className="oa-modal-footer">
              <button className="oa-btn-ghost" onClick={() => setShowDeptModal(false)}>Cancelar</button>
              <button className="oa-btn-cyan" onClick={createDept} disabled={!newDept.name.trim()}>
                <Plus size={13} /> Crear
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
      {showEmpModal && (
  <ModalOverlay className="oa-modal-overlay" label={editingEmp ? 'Editar empleado' : 'Nuevo empleado'} onClose={() => setShowEmpModal(false)}>
    <div className="oa-modal" style={{ maxWidth: 480 }}>
      <div className="oa-modal-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
            {editingEmp ? 'Editar Empleado' : 'Nuevo Empleado'}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            {editingEmp ? `Editando: ${editingEmp.name}` : 'Añadir a la organización'}
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowEmpModal(false)} style={{ padding: '8px' }}><X size={16} /></button>
      </div>
      <div className="oa-modal-body oa-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Nombre completo *</div>
            <input className="oa-input" placeholder="Ana García" value={empForm.name}
              onChange={e => setEmpForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Email *</div>
            <input className="oa-input" type="email" placeholder="ana@empresa.com" value={empForm.email}
              onChange={e => setEmpForm(p => ({ ...p, email: e.target.value }))} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>Departamento</div>
            <select className="oa-input" value={empForm.department_id}
              onChange={e => setEmpForm(p => ({ ...p, department_id: e.target.value }))}>
              <option value="">— Sin departamento —</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>DNI (opcional)</div>
            <input className="oa-input" placeholder="12345678A" value={empForm.dni}
              onChange={e => setEmpForm(p => ({ ...p, dni: e.target.value }))} />
          </div>
        </div>
        {editingEmp && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 0' }}>
            <button
              className={`oa-toggle ${empForm.active ? 'on' : 'off'}`}
              onClick={() => setEmpForm(p => ({ ...p, active: !p.active }))}
              type="button"
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
              Empleado {empForm.active ? 'activo' : 'inactivo'}
            </span>
          </label>
        )}
      </div>
      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={() => setShowEmpModal(false)}>Cancelar</button>
        <button className="oa-btn-cyan" onClick={saveEmp} disabled={!empForm.name.trim() || !empForm.email.trim()}>
          {editingEmp ? <><UserCog size={13} /> Guardar cambios</> : <><UserPlus size={13} /> Crear empleado</>}
        </button>
      </div>
    </div>
  </ModalOverlay>
)}

{showBulkModal && (
  <ModalOverlay className="oa-modal-overlay" label="Importar empleados desde Excel" onClose={() => setShowBulkModal(false)}>
    <div className="oa-modal" style={{ maxWidth: 640 }}>
      <div className="oa-modal-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Importar desde Excel</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            Columnas: nombre · apellido · email · departamento · dni
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowBulkModal(false)} style={{ padding: '8px' }}><X size={16} /></button>
      </div>
      <div className="oa-modal-body oa-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', border: '2px dashed rgba(0,255,255,0.25)', borderRadius: 14, cursor: 'pointer', background: 'rgba(0,255,255,0.03)' }}>
          <FileSpreadsheet size={24} color="#00FFFF" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            {bulkFile
              ? <><p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{bulkFile.name}</p>
                  <p style={{ fontSize: 11, color: '#00FFFF' }}>{bulkPreview.length} empleados detectados</p></>
              : <><p style={{ fontSize: 13, fontWeight: 700, color: '#fff', marginBottom: 2 }}>Seleccionar archivo .xlsx o .csv</p>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Descarga la plantilla para ver el formato</p></>
            }
          </div>
          <input type="file" accept=".xlsx,.csv" style={{ display: 'none' }}
            onChange={e => parseBulkFile(e.target.files[0])} />
        </label>

        {bulkPreview.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 10 }}>
              Preview — {bulkPreview.length} filas
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }} className="oa-scroll">
              {bulkPreview.slice(0, 8).map((r, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
                  <span style={{ color: '#fff', fontWeight: 600 }}>{r.name}</span>
                  <span style={{ color: 'rgba(255,255,255,0.5)' }}>{r.email}</span>
                  <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{r.department_name || '—'}</span>
                </div>
              ))}
              {bulkPreview.length > 8 && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '8px 0' }}>
                  ...y {bulkPreview.length - 8} más
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={downloadTemplate}><Download size={12} /> Plantilla</button>
        <button className="oa-btn-ghost" onClick={() => setShowBulkModal(false)}>Cancelar</button>
        <button className="oa-btn-cyan" onClick={confirmBulkImport}
          disabled={!bulkPreview.length || bulkImporting}>
          {bulkImporting
            ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Importando...</>
            : <><UserPlus size={13} /> Importar {bulkPreview.length} empleados</>}
        </button>
      </div>
    </div>
  </ModalOverlay>
)}
{showSendCodeModal && (
  <ModalOverlay className="oa-modal-overlay" label="Enviar código de acceso" onClose={() => setShowSendCodeModal(false)}>
    <div className="oa-modal" style={{ maxWidth: 420 }}>
      <div className="oa-modal-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Enviar código de acceso</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#FFA500', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            El empleado recibirá su código por email
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowSendCodeModal(false)} style={{ padding: '8px' }}><X size={16} /></button>
      </div>
      <div className="oa-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Opciones de destino — solo visibles cuando se abre desde "Enviar códigos" global */}
        {sendCodeTarget.type === 'all' && (
          <>
            {[
              { type: 'all', label: 'Todos los pendientes', sub: `${users.filter(u => !u.code_sent_at).length} empleados sin código`},
            ].map(o => (
              <button key={o.type} type="button"
  onClick={() => setSendCodeTarget({ type: o.type, id: null })}
  aria-pressed={sendCodeTarget.type === o.type}
  style={{ width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit', padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
    border: `1px solid ${sendCodeTarget.type === o.type ? 'rgba(255,165,0,0.5)' : 'rgba(255,255,255,0.08)'}`,
    background: sendCodeTarget.type === o.type ? 'rgba(255,165,0,0.07)' : 'transparent' }}>
  <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#fff' }}>{o.label}</span>
  <span style={{ display: 'block', fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{o.sub}</span>
</button>
            ))}
            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '4px 0 6px' }}>O por departamento</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }} className="oa-scroll">
              {departments.map(d => (
                <button key={d.id} type="button"
  onClick={() => setSendCodeTarget({ type: 'dept', id: d.id })}
  aria-pressed={sendCodeTarget.id === d.id}
  style={{ width: '100%', textAlign: 'left', font: 'inherit', padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    border: `1px solid ${sendCodeTarget.id === d.id ? 'rgba(255,165,0,0.5)' : 'rgba(255,255,255,0.06)'}`,
    background: sendCodeTarget.id === d.id ? 'rgba(255,165,0,0.07)' : 'rgba(255,255,255,0.02)' }}>
  <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{d.name}</span>
  <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{d.user_count} empleados</span>
</button>
              ))}
            </div>
          </>
        )}
        {sendCodeTarget.type === 'user' && (
          <div style={{ padding: '14px 16px', background: 'rgba(255,165,0,0.06)', border: '1px solid rgba(255,165,0,0.25)', borderRadius: 12 }}>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>
              Enviar código a: <span style={{ color: '#FFA500' }}>
                {users.find(u => u.id === sendCodeTarget.id)?.name || 'este empleado'}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
              {users.find(u => u.id === sendCodeTarget.id)?.email}
            </div>
          </div>
        )}
      </div>
      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={() => setShowSendCodeModal(false)}>Cancelar</button>
        <button onClick={sendCodes} disabled={sendingCode}
          style={{ background: '#FFA500', color: '#000', fontWeight: 800, border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: sendingCode ? 'not-allowed' : 'pointer', opacity: sendingCode ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'DM Sans, sans-serif' }}>
          {sendingCode ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Enviando...</> : <><Mail size={13} /> Enviar código(s)</>}
        </button>
      </div>
    </div>
  </ModalOverlay>
)}
{showEvalModal && (
  <ModalOverlay className="oa-modal-overlay" label="Evaluación del módulo" onClose={() => setShowEvalModal(false)}>
    <div className="oa-modal" style={{ maxWidth: 520 }}>
      <div className="oa-modal-header" style={{ borderColor: 'rgba(204,255,0,0.2)' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>Evaluación del Módulo</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#ccff00', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            {evalModuleTitle}
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowEvalModal(false)} style={{ padding: '8px' }}>
          <X size={16} />
        </button>
      </div>

      <div className="oa-modal-body oa-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ padding: '14px 16px', background: 'rgba(204,255,0,0.05)', border: '1px solid rgba(204,255,0,0.2)', borderRadius: 12 }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
            Genera un <strong style={{ color: '#ccff00' }}>escenario de roleplay</strong> con IA basado en el contenido del módulo. Los empleados deberán completarlo aplicando lo aprendido.
          </div>
        </div>

        {roleplayResult ? (
          <div style={{ padding: '16px', background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <CheckCircle2 size={16} color="#00FF88" />
              <span style={{ fontSize: 13, fontWeight: 800, color: '#00FF88' }}>{roleplayResult.title}</span>
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 10 }}>{roleplayResult.description}</p>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
              {roleplayResult.steps?.length || 0} pasos · {roleplayResult.learning_objective}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(roleplayResult.steps || []).map((step, i) => (
                <div key={step.id} style={{ padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, borderLeft: '2px solid rgba(204,255,0,0.4)' }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: '#ccff00', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Paso {i + 1}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{step.situation}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
            No hay roleplay generado aún para este módulo.
          </div>
        )}
      </div>

      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={() => setShowEvalModal(false)}>Cerrar</button>
        <button
          onClick={generateRoleplay}
          disabled={generatingRoleplay}
          style={{
            background: '#ccff00', color: '#000', fontWeight: 800, border: 'none',
            borderRadius: 10, padding: '10px 20px', fontSize: 11, letterSpacing: '0.1em',
            textTransform: 'uppercase', cursor: generatingRoleplay ? 'not-allowed' : 'pointer',
            opacity: generatingRoleplay ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 7,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          {generatingRoleplay
            ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Generando...</>
            : <><Brain size={13} /> {roleplayResult ? 'Regenerar Roleplay' : 'Generar Roleplay'}</>
          }
        </button>
      </div>
    </div>
  </ModalOverlay>
)}
{showRenameModal && (
  <ModalOverlay className="oa-modal-overlay" label="Renombrar" onClose={() => setShowRenameModal(false)}>
    <div className="oa-modal" style={{ maxWidth: 440 }}>
      <div className="oa-modal-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
            Renombrar {renameTarget.type === 'module' ? 'Módulo' : 'Unidad'}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            {renameTarget.title}
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowRenameModal(false)} style={{ padding: '8px' }}>
          <X size={16} />
        </button>
      </div>
      <div className="oa-modal-body">
        <div style={{ fontSize: 10, fontWeight: 800, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
          Nuevo nombre *
        </div>
        <input
          className="oa-input"
          value={renameValue}
          onChange={e => setRenameValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && saveRename()}
          autoFocus
        />
      </div>
      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={() => setShowRenameModal(false)}>Cancelar</button>
        <button className="oa-btn-cyan" onClick={saveRename} disabled={!renameValue.trim() || renameSaving}>
          {renameSaving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Edit size={13} />}
          Guardar
        </button>
      </div>
    </div>
  </ModalOverlay>
)}
{showDiplomaConfig && diplomaConfigModule && (
  <DiplomaConfigModal
    open={showDiplomaConfig}
    onClose={() => setShowDiplomaConfig(false)}
    moduleId={diplomaConfigModule.id}
    moduleTitle={diplomaConfigModule.title}
    initialConfig={diplomaConfigModule.diplomaConfig}
    api={api}
    onSaved={() => {
      setShowDiplomaConfig(false);
      fetchAll();
    }}
  />
)}

    {showOrgDiploma && (
  <DiplomaConfigModal
    open={showOrgDiploma}
    onClose={() => setShowOrgDiploma(false)}
    scope="organization"
    organizationId={orgId}
    orgName={orgData?.name}
    initialConfig={orgData?.diplomaConfig}
    api={api}
    onSaved={() => {
      setShowOrgDiploma(false);
      fetchAll();
    }}
  />
)}
{showEmpReport && (
  <ModalOverlay className="oa-modal-overlay" label="Informe del empleado" onClose={() => setShowEmpReport(false)}>
    <div className="oa-modal" style={{ maxWidth: 620 }}>
      <div className="oa-modal-header">
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: '#fff' }}>
            {empReport?.user?.name || 'Informe del empleado'}
          </div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#00FFFF', textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 3 }}>
            {empReport?.overall_avg_score != null
              ? `Nota media general: ${empReport.overall_avg_score}%`
              : 'Sin evaluaciones aún'}
          </div>
        </div>
        <button className="oa-btn-ghost" onClick={() => setShowEmpReport(false)} style={{ padding: '8px' }}>
          <X size={16} />
        </button>
      </div>

      <div className="oa-modal-body oa-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loadingReport && (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Loader2 size={24} className="animate-spin" color="#00FFFF" />
          </div>
        )}

        {!loadingReport && empReport?.modules?.length === 0 && (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: 30 }}>
            Este empleado no tiene cursos asignados.
          </div>
        )}

        {!loadingReport && empReport?.modules?.map(m => (
          <div key={m.module_id} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{m.title}</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <Badge color={m.status === 'completed' ? '#00FF88' : '#FFA500'}>
                    {m.status === 'completed' ? 'Completado' : m.status === 'in_progress' ? 'En progreso' : 'Pendiente'}
                  </Badge>
                  {m.avg_score != null && (
                    <Badge color={m.avg_score >= 70 ? '#00FF88' : m.avg_score >= 40 ? '#ccff00' : '#FF4444'}>
                      Nota media: {m.avg_score}%
                    </Badge>
                  )}
                </div>
              </div>
              <button className="oa-btn-danger" onClick={() => unassignModule(empReport.user.id, m.module_id)} title="Desasignar curso">
                <UserMinus size={13} />
              </button>
            </div>

            {m.strengths.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 9, fontWeight: 800, color: '#00FF88', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Fortalezas</div>
                {m.strengths.map((s, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>• {s}</div>
                ))}
              </div>
            )}
            {m.weaknesses.length > 0 && (
              <div>
                <div style={{ fontSize: 9, fontWeight: 800, color: '#FF7300', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>A mejorar</div>
                {m.weaknesses.map((s, i) => (
                  <div key={i} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 2 }}>• {s}</div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="oa-modal-footer">
        <button className="oa-btn-ghost" onClick={() => setShowEmpReport(false)}>Cerrar</button>
      </div>
    </div>
  </ModalOverlay>
)}
</>
  );
};

export default OrgAdminPanel;